import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Lucide from 'lucide-react';
import { codexApi } from '../api';
import { bigCelebrate } from '../../lib/celebrate';

const TIER_STYLES = {
  common: {
    ring: 'rgba(212,175,55,0.45)',
    glow: '0 0 20px rgba(212,175,55,0.3)',
    label: 'COMMON',
    accent: '#d4af37',
  },
  rare: {
    ring: 'rgba(168,85,247,0.6)',
    glow: '0 0 28px rgba(168,85,247,0.45)',
    label: 'RARE',
    accent: '#a855f7',
  },
  epic: {
    ring: 'rgba(234,88,12,0.65)',
    glow: '0 0 32px rgba(234,88,12,0.55)',
    label: 'EPIC',
    accent: '#ea580c',
  },
  legendary: {
    ring: 'rgba(245,217,124,0.85)',
    glow: '0 0 44px rgba(245,217,124,0.7), 0 0 96px rgba(168,85,247,0.4)',
    label: 'LEGENDARY',
    accent: '#f5d97c',
  },
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
};

function BadgeCard({ b, onOpen }) {
  const tier = TIER_STYLES[b.tier] || TIER_STYLES.common;
  const Icon = Lucide[b.icon] || Lucide.Award;
  const unlocked = !!b.unlocked;

  return (
    <motion.button
      type="button"
      data-testid={`codex-badge-${b.id}`}
      onClick={() => onOpen(b)}
      whileHover={{ y: -3, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 380, damping: 22 }}
      className="codex-card"
      style={{
        position: 'relative',
        padding: 18,
        textAlign: 'center',
        cursor: 'pointer',
        borderRadius: 18,
        border: '1px solid ' + (unlocked ? tier.ring : 'rgba(245,217,124,0.10)'),
        background: unlocked
          ? `radial-gradient(110% 80% at 50% 0%, ${tier.accent}22 0%, transparent 70%), linear-gradient(180deg, rgba(245,217,124,0.06), rgba(168,85,247,0.04))`
          : 'linear-gradient(180deg, rgba(20,16,43,0.6), rgba(11,10,22,0.7))',
        boxShadow: unlocked ? tier.glow : 'inset 0 0 30px rgba(0,0,0,0.4)',
        color: 'inherit',
        font: 'inherit',
        overflow: 'hidden',
      }}
    >
      {unlocked && (
        <motion.div
          aria-hidden
          initial={{ opacity: 0.0 }}
          animate={{ opacity: [0.15, 0.35, 0.15] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: `radial-gradient(60% 50% at 50% 0%, ${tier.accent}33, transparent 70%)`,
          }}
        />
      )}

      <div style={{ position: 'relative', zIndex: 2 }}>
        <div
          style={{
            width: 84, height: 84, borderRadius: '50%', margin: '0 auto 12px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: unlocked
              ? `radial-gradient(circle at 50% 35%, ${tier.accent}99, ${tier.accent}33 60%, transparent 75%)`
              : 'radial-gradient(circle at 50% 35%, rgba(120,120,140,0.18), transparent 70%)',
            border: `2px solid ${unlocked ? tier.accent : 'rgba(120,120,140,0.25)'}`,
            filter: unlocked ? 'none' : 'grayscale(1) brightness(0.55)',
          }}
        >
          <Icon
            size={42}
            color={unlocked ? '#fff7d6' : '#7a7a8a'}
            strokeWidth={unlocked ? 2 : 1.6}
            style={{ filter: unlocked ? 'drop-shadow(0 0 6px rgba(255,247,214,0.8))' : 'none' }}
          />
        </div>

        <div className="codex-display" style={{
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: '0.05em',
          color: unlocked ? 'var(--codex-gold-light)' : 'rgba(245,217,124,0.45)',
          marginBottom: 4,
          minHeight: 36,
        }}>
          {unlocked ? b.name : '???'}
        </div>

        <div style={{
          fontSize: 11,
          color: unlocked ? 'rgba(253,246,227,0.75)' : 'rgba(245,217,124,0.4)',
          lineHeight: 1.4,
          minHeight: 32,
        }}>
          {unlocked ? b.description : b.how}
        </div>

        <div style={{
          marginTop: 10,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '3px 10px',
          borderRadius: 999,
          fontSize: 9,
          letterSpacing: '0.2em',
          fontFamily: "'Cinzel', serif",
          background: unlocked ? `${tier.accent}22` : 'rgba(120,120,140,0.1)',
          color: unlocked ? tier.accent : 'rgba(245,217,124,0.45)',
          border: `1px solid ${unlocked ? tier.accent + '55' : 'rgba(120,120,140,0.25)'}`,
        }}>
          {tier.label}
        </div>

        {unlocked && b.earned_at && (
          <div style={{ marginTop: 8, fontSize: 10, color: 'rgba(253,246,227,0.5)', letterSpacing: '0.05em' }}>
            Earned {formatDate(b.earned_at)}
          </div>
        )}
      </div>
    </motion.button>
  );
}

function BadgeDetailModal({ badge, onClose }) {
  if (!badge) return null;
  const tier = TIER_STYLES[badge.tier] || TIER_STYLES.common;
  const Icon = Lucide[badge.icon] || Lucide.Award;
  const unlocked = !!badge.unlocked;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="codex-badge-modal"
        style={{
          position: 'fixed', inset: 0, zIndex: 100,
          background: 'rgba(11,10,22,0.85)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}
      >
        <motion.div
          initial={{ scale: 0.85, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.85, opacity: 0 }}
          transition={{ type: 'spring', damping: 22 }}
          onClick={(e) => e.stopPropagation()}
          className="codex-card"
          style={{
            maxWidth: 460, width: '100%',
            padding: 32, textAlign: 'center',
            border: `2px solid ${unlocked ? tier.accent : 'rgba(120,120,140,0.3)'}`,
            background: unlocked
              ? `radial-gradient(120% 80% at 50% 0%, ${tier.accent}33 0%, transparent 70%), linear-gradient(180deg, #1a0e2c, #0b0a16)`
              : 'linear-gradient(180deg, #1a0e2c, #0b0a16)',
            boxShadow: unlocked ? tier.glow : 'none',
          }}
        >
          <div
            style={{
              width: 130, height: 130, borderRadius: '50%', margin: '0 auto 16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: unlocked
                ? `radial-gradient(circle at 50% 35%, ${tier.accent}aa, ${tier.accent}33 60%, transparent 75%)`
                : 'radial-gradient(circle at 50% 35%, rgba(120,120,140,0.2), transparent 70%)',
              border: `3px solid ${unlocked ? tier.accent : 'rgba(120,120,140,0.3)'}`,
              filter: unlocked ? 'none' : 'grayscale(1) brightness(0.5)',
            }}
          >
            <Icon size={64} color={unlocked ? '#fff7d6' : '#7a7a8a'} strokeWidth={unlocked ? 2 : 1.6} />
          </div>

          <div className="codex-eyebrow" style={{ color: tier.accent, marginBottom: 6 }}>
            {tier.label} · {badge.category}
          </div>
          <div className="codex-display" style={{ fontSize: 26, color: 'var(--codex-gold-light)', marginBottom: 8 }}>
            {unlocked ? badge.name : 'Sealed'}
          </div>
          <div style={{ color: 'rgba(253,246,227,0.85)', marginBottom: 16, lineHeight: 1.5 }}>
            {unlocked ? badge.description : 'This achievement remains hidden until earned.'}
          </div>

          <div style={{
            background: 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(245,217,124,0.18)',
            borderRadius: 10,
            padding: 14,
            marginBottom: 18,
            fontSize: 12,
            color: 'rgba(253,246,227,0.75)',
          }}>
            <div className="codex-eyebrow" style={{ marginBottom: 6, fontSize: 9 }}>How to earn</div>
            {badge.how}
          </div>

          {unlocked && badge.earned_at && (
            <div style={{ fontSize: 11, color: 'rgba(245,217,124,0.7)', marginBottom: 14, letterSpacing: '0.05em' }}>
              Earned on {formatDate(badge.earned_at)}
            </div>
          )}

          <button
            onClick={onClose}
            className="codex-btn codex-btn-ghost"
            data-testid="codex-badge-modal-close"
          >
            Close
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function NewlyUnlockedFanfare({ badges, onDone }) {
  if (!badges || badges.length === 0) return null;
  const [idx, setIdx] = useState(0);
  const current = badges[idx];

  useEffect(() => {
    bigCelebrate();
  }, []);

  if (!current) return null;
  const tier = TIER_STYLES[current.tier] || TIER_STYLES.common;
  const Icon = Lucide[current.icon] || Lucide.Award;

  const next = () => {
    if (idx + 1 < badges.length) {
      setIdx(idx + 1);
      bigCelebrate();
    } else {
      onDone();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="codex-badge-fanfare"
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(11,10,22,0.92)', backdropFilter: 'blur(12px)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <motion.div
        key={current.id}
        initial={{ scale: 0.4, opacity: 0, rotate: -8 }}
        animate={{ scale: 1, opacity: 1, rotate: 0 }}
        transition={{ type: 'spring', damping: 14 }}
        style={{ textAlign: 'center', maxWidth: 480 }}
      >
        <div className="codex-eyebrow" style={{ color: tier.accent, marginBottom: 14, letterSpacing: '0.4em' }}>
          A NEW SEAL HAS BEEN BROKEN
        </div>
        <motion.div
          animate={{ boxShadow: [tier.glow, `0 0 80px ${tier.accent}`, tier.glow] }}
          transition={{ duration: 2, repeat: Infinity }}
          style={{
            width: 180, height: 180, borderRadius: '50%', margin: '0 auto 22px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `radial-gradient(circle at 50% 35%, ${tier.accent}cc, ${tier.accent}33 60%, transparent 75%)`,
            border: `4px solid ${tier.accent}`,
          }}
        >
          <Icon size={88} color="#fff7d6" strokeWidth={2} />
        </motion.div>
        <div className="codex-display" style={{ fontSize: 36, color: 'var(--codex-gold-light)', marginBottom: 10 }}>
          {current.name}
        </div>
        <div style={{ color: 'rgba(253,246,227,0.85)', marginBottom: 24, fontSize: 16 }}>
          {current.description}
        </div>
        <button onClick={next} className="codex-btn codex-btn-primary" data-testid="codex-fanfare-continue">
          {idx + 1 < badges.length ? `Next (${idx + 1}/${badges.length})` : 'Glory be to His name'}
        </button>
      </motion.div>
    </motion.div>
  );
}

export default function CodexAchievements() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // all | earned | locked | <category>
  const [openBadge, setOpenBadge] = useState(null);
  const [fanfareBadges, setFanfareBadges] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await codexApi.badges();
        if (cancelled) return;
        setData(d);
        if (d.newly_unlocked_ids?.length) {
          const newOnes = d.earned.filter((b) => d.newly_unlocked_ids.includes(b.id));
          setFanfareBadges(newOnes);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const tabs = useMemo(() => {
    const out = [{ id: 'all', label: 'All' }, { id: 'earned', label: 'Earned' }, { id: 'locked', label: 'Locked' }];
    if (data?.categories) data.categories.forEach((c) => out.push({ id: c.name, label: c.name }));
    return out;
  }, [data]);

  const visibleBadges = useMemo(() => {
    if (!data) return [];
    if (activeTab === 'all') return [...data.earned, ...data.locked];
    if (activeTab === 'earned') return data.earned;
    if (activeTab === 'locked') return data.locked;
    const cat = data.categories.find((c) => c.name === activeTab);
    return cat ? cat.badges : [];
  }, [data, activeTab]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: 'var(--codex-gold-light)' }} data-testid="codex-achievements-loading">
        Polishing the seals...
      </div>
    );
  }
  if (!data) return null;

  const pct = data.stats.completion_pct;

  return (
    <div data-testid="codex-achievements-page">
      <div style={{ marginBottom: 22 }}>
        <div className="codex-eyebrow" style={{ marginBottom: 6 }}>HALL OF SEALS</div>
        <h1 className="codex-h1" style={{ marginBottom: 8 }}>My Achievements</h1>
        <p style={{ color: 'rgba(253,246,227,0.75)', maxWidth: 640 }}>
          Every seal you break tells a story of faithfulness. Walk slowly through the hall — each is a stone of remembrance.
        </p>
      </div>

      {/* Progress strip */}
      <div className="codex-card" style={{ padding: 18, marginBottom: 22 }} data-testid="codex-achievements-progress">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 12 }}>
          <div className="codex-display" style={{ color: 'var(--codex-gold-light)', letterSpacing: '0.15em' }}>
            {data.stats.earned_count} <span style={{ color: 'rgba(253,246,227,0.55)' }}>/ {data.stats.total} broken</span>
          </div>
          <div style={{ fontFamily: "'Cinzel', serif", color: 'var(--codex-gold)', fontSize: 26 }}>
            {pct}%
          </div>
        </div>
        <div style={{ height: 10, background: 'rgba(0,0,0,0.4)', borderRadius: 999, overflow: 'hidden', border: '1px solid rgba(245,217,124,0.18)' }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 1.4, ease: 'easeOut' }}
            style={{
              height: '100%',
              background: 'linear-gradient(90deg, var(--codex-gold) 0%, var(--codex-orange) 60%, var(--codex-purple-light) 100%)',
              boxShadow: '0 0 18px rgba(212,175,55,0.4)',
            }}
          />
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }} data-testid="codex-achievements-tabs">
        {tabs.map((t) => {
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`codex-achievements-tab-${t.id}`}
              className="codex-btn"
              style={{
                padding: '8px 14px',
                fontSize: 12,
                background: active ? 'rgba(212,175,55,0.18)' : 'rgba(245,217,124,0.04)',
                color: active ? 'var(--codex-gold-light)' : 'rgba(253,246,227,0.7)',
                borderColor: active ? 'rgba(212,175,55,0.5)' : 'rgba(245,217,124,0.15)',
                textTransform: 'none',
                letterSpacing: '0.08em',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Grid */}
      {visibleBadges.length === 0 ? (
        <div className="codex-card" style={{ padding: 36, textAlign: 'center' }}>
          <Lucide.Sparkles size={36} color="var(--codex-gold)" style={{ margin: '0 auto 8px' }} />
          <div style={{ color: 'rgba(253,246,227,0.7)' }}>No achievements in this view yet — keep walking the path.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 14 }}>
          {visibleBadges.map((b, i) => (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.025, 0.6) }}
            >
              <BadgeCard b={b} onOpen={setOpenBadge} />
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {openBadge && <BadgeDetailModal badge={openBadge} onClose={() => setOpenBadge(null)} />}
      </AnimatePresence>

      <AnimatePresence>
        {fanfareBadges.length > 0 && (
          <NewlyUnlockedFanfare
            badges={fanfareBadges}
            onDone={() => setFanfareBadges([])}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
